import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { SEARCH_AREAS, PRICE_STEPS } from "../lib/searchAreas";

const fmtM = (n) => `AED ${n >= 1000000 ? n / 1000000 + "M" : n.toLocaleString()}`;

// Portal-style search bar (31 Oct 2026). Submits to /search with URL params.
export default function HomeSearch({ compact = false, initial = {} }) {
  const navigate = useNavigate();
  const [type, setType] = useState(initial.type || "any");
  const [area, setArea] = useState(initial.area || "");
  const [max, setMax] = useState(initial.max || "");
  const [beds, setBeds] = useState(initial.beds || "");

  const submit = (e) => {
    e.preventDefault();
    const q = new URLSearchParams();
    if (type !== "any") q.set("type", type);
    if (area) q.set("area", area);
    if (max) q.set("max", max);
    if (beds) q.set("beds", beds);
    navigate(`/search${q.toString() ? "?" + q.toString() : ""}`);
  };

  const sel = `w-full bg-transparent ${compact ? "text-ink" : "text-bone"} text-[0.95rem] py-3 pr-6 focus:outline-none cursor-pointer appearance-none [&>option]:text-ink`;
  const lab = `block text-[0.6rem] tracking-[0.14em] uppercase ${compact ? "text-ink/50" : "text-bone/65"} mb-0.5`;
  return (
    <form onSubmit={submit} className={`w-full ${compact ? "" : "max-w-4xl"}`} data-testid="home-search" role="search" aria-label="Search properties">
      <div className="flex gap-1 mb-2" role="tablist" aria-label="Property type">
        {[["any", "All"], ["ready", "Ready"], ["offplan", "Off-plan"]].map(([v, l]) => (
          <button key={v} type="button" role="tab" aria-selected={type === v} onClick={() => setType(v)}
            className={`px-4 py-2 text-[0.7rem] tracking-[0.14em] uppercase transition-colors ${type === v ? "bg-bone text-ink" : "bg-ink/40 text-bone/80 hover:text-bone backdrop-blur-sm"}`}
            data-testid={`search-type-${v}`}>{l}</button>
        ))}
      </div>
      <div className={`${compact ? "bg-bone" : "bg-ink/25 backdrop-blur-md border border-bone/25"} shadow-2xl grid grid-cols-2 md:grid-cols-[1.4fr_1fr_0.8fr_auto] items-stretch`}>
        <label className="col-span-2 md:col-span-1 px-5 pt-3 border-b md:border-b-0 md:border-r border-bone/20">
          <span className={lab}>Location</span>
          <select value={area} onChange={(e) => setArea(e.target.value)} className={sel} aria-label="Location" data-testid="search-area">
            <option value="">All Dubai &amp; RAK</option>
            {SEARCH_AREAS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </label>
        <label className="px-5 pt-3 border-r border-bone/20">
          <span className={lab}>Max price</span>
          <select value={max} onChange={(e) => setMax(e.target.value)} className={sel} aria-label="Maximum price" data-testid="search-max">
            <option value="">Any price</option>
            {PRICE_STEPS.map((p) => <option key={p} value={p}>{fmtM(p)}</option>)}
          </select>
        </label>
        <label className="px-5 pt-3">
          <span className={lab}>Bedrooms</span>
          <select value={beds} onChange={(e) => setBeds(e.target.value)} className={sel} aria-label="Bedrooms" data-testid="search-beds">
            <option value="">Any</option>
            <option value="0">Studio</option>
            {[1, 2, 3, 4].map((b) => <option key={b} value={b}>{b}{b === 4 ? "+" : ""} bed</option>)}
          </select>
        </label>
        <button type="submit" className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-2 bg-gold hover:bg-ink hover:text-bone text-ink px-8 py-4 text-[0.72rem] tracking-[0.16em] uppercase font-medium transition-colors" data-testid="search-submit">
          <Search size={16} /> Search
        </button>
      </div>
    </form>
  );
}
